import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion } from "framer-motion";
import { Button } from "../../../components/ui/button";
import Footer from "../../../components/landing/Footer";

const Privacy: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border"
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 md:h-20">
            <motion.div className="flex items-center gap-2" whileHover={{ scale: 1.02 }}>
              <Link to="/" className="text-2xl md:text-3xl font-bold">
                <span className="text-primary">Social</span>
                <span className="bg-secondary text-secondary-foreground px-2 py-0.5 rounded-md ml-1">Bunkr</span>
              </Link>
            </motion.div>
            <nav className="hidden md:flex items-center gap-8">
              {["Home", "About", "Contact"].map((item) => (
                <Link
                  key={item}
                  to={item === "Home" ? "/" : `/${item.toLowerCase()}`}
                  className="text-foreground/80 hover:text-primary font-medium transition-colors"
                >
                  {item}
                </Link>
              ))}
            </nav>
            <div className="hidden md:flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-foreground hover:text-primary">Login</Button>
              </Link>
              <Link to="/signup">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90">Sign Up</Button>
              </Link>
            </div>
            <button className="md:hidden p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
          {isMobileMenuOpen && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="md:hidden py-4 space-y-3">
              {["Home", "About", "Contact"].map((item) => (
                <Link key={item} to={item === "Home" ? "/" : `/${item.toLowerCase()}`} className="block text-foreground/80 hover:text-primary font-medium" onClick={() => setIsMobileMenuOpen(false)}>
                  {item}
                </Link>
              ))}
              <div className="flex gap-3 pt-2">
                <Link to="/login"><Button variant="ghost" size="sm">Login</Button></Link>
                <Link to="/signup"><Button size="sm">Sign Up</Button></Link>
              </div>
            </motion.div>
          )}
        </div>
      </motion.header>

      <main className="flex-1 pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <h1 className="text-3xl md:text-4xl font-bold text-primary mb-2">Privacy Policy</h1>
          <p className="text-sm text-muted-foreground mb-8">Last updated: April 6, 2026</p>

          <div className="bg-white rounded-xl shadow-sm p-6 md:p-10 space-y-8 text-foreground/80 leading-relaxed">
            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">1. Introduction</h2>
              <p>
                Social Bunkr ("we", "our", or "us") operates the Social Bunkr mobile application and website
                (collectively, the "Service"). This Privacy Policy explains how we collect, use, disclose, and
                safeguard your information when you use our Service. By using the Service, you agree to the
                practices described in this policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">2. Information We Collect</h2>
              <h3 className="font-medium text-foreground mt-4 mb-2">Personal Information</h3>
              <p>When you register for an account or use our Service, we may collect:</p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li>Name, email address, and phone number</li>
                <li>Profile information and photographs</li>
                <li>Payment and billing information (processed securely via Razorpay)</li>
                <li>Property listing details (for hosts)</li>
                <li>Booking and reservation history</li>
              </ul>
              <h3 className="font-medium text-foreground mt-4 mb-2">Automatically Collected Information</h3>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li>Device information (model, operating system, unique identifiers)</li>
                <li>Usage data (pages visited, features used, interaction patterns)</li>
                <li>Log data (IP address, browser type, access times)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">3. How We Use Your Information</h2>
              <ul className="list-disc ml-6 space-y-1">
                <li>To provide, maintain, and improve the Service</li>
                <li>To process bookings, payments, and transactions</li>
                <li>To communicate with you about your account, bookings, and updates</li>
                <li>To verify host and guest identities</li>
                <li>To detect, prevent, and address fraud or security issues</li>
                <li>To comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">4. Information Sharing</h2>
              <p>We do not sell your personal information. We may share your information with:</p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li><strong>Other users:</strong> Hosts and guests see relevant booking details to facilitate stays</li>
                <li><strong>Payment processors:</strong> Razorpay processes payments on our behalf</li>
                <li><strong>Service providers:</strong> Cloud hosting (Google Cloud Platform) and analytics services</li>
                <li><strong>Legal requirements:</strong> When required by law, regulation, or legal process</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">5. Data Security</h2>
              <p>
                We implement industry-standard security measures to protect your data, including encrypted
                storage, secure API communication (HTTPS), and token-based authentication. However, no
                method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">6. Data Retention</h2>
              <p>
                We retain your personal information for as long as your account is active or as needed to provide
                the Service. You may request deletion of your account and associated data by contacting us.
                Some information may be retained as required by law or for legitimate business purposes.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">7. Your Rights</h2>
              <p>You have the right to:</p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li>Access, update, or delete your personal information</li>
                <li>Withdraw consent for data processing</li>
                <li>Request a copy of your data</li>
                <li>Object to processing of your personal information</li>
                <li>Lodge a complaint with a data protection authority</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">8. Third-Party Services</h2>
              <p>
                Our Service may contain links to third-party websites or services. We are not responsible for
                the privacy practices of these third parties. We encourage you to review their privacy policies.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">9. Children's Privacy</h2>
              <p>
                Our Service is not intended for users under the age of 18. We do not knowingly collect personal
                information from children. If we become aware that we have collected data from a child, we will
                take steps to delete it promptly.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">10. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any changes by
                posting the new policy on this page and updating the "Last updated" date. Continued use of
                the Service after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3">11. Contact Us</h2>
              <p>If you have any questions about this Privacy Policy, please contact us:</p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li>Email: <a href="mailto:support@socialbunkr.in" className="text-primary hover:underline">support@socialbunkr.in</a></li>
                <li>Website: <a href="https://socialbunkr.in/contact" className="text-primary hover:underline">socialbunkr.in/contact</a></li>
              </ul>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Privacy;
